import { useState, useEffect, useRef } from 'react'
import { useAppSelector, useAppDispatch } from '../hooks/redux'
import { usePermissions, Permission } from '../hooks/usePermissions'
import PermissionGate from '../components/PermissionGate'
import { useToast } from '../components/ToastContainer'
import { PencilIcon, TrashIcon, UserPlusIcon, ArchiveBoxIcon, PhotoIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline'
import * as projectApi from '../services/api/project'
import EditableField from '../components/EditableField'
import InviteMemberModal from '../components/InviteMemberModal'
import { getImageUrl } from '../utils/formatters'
import { setCurrentProject } from '../store/slices/projectSlice'
import { getProjectAvailableRoles } from '../services/api/role'
import { CustomRole } from '@/types'
import { useNavigate } from 'react-router-dom'
import * as customFieldApi from '../services/api/customField'

// 專案角色選項
const PROJECT_ROLES = [
  { value: 'OWNER', label: '專案擁有者' },
  { value: 'ADMIN', label: '專案管理員' },
  { value: 'MEMBER', label: '一般成員' },
  { value: 'VIEWER', label: '唯讀權限' },
  { value: 'PROPERTY_MANAGER', label: '物業經理' },
  { value: 'ASSISTANT_MANAGER', label: '物業副理' },
  { value: 'PROPERTY_STAFF', label: '物業執行人員' },
  { value: 'CONTRACTOR_LEAD', label: '外包主管' },
  { value: 'CONTRACTOR', label: '外包人員' },
  { value: 'COMMITTEE_CHAIRMAN', label: '管委會主委' },
  { value: 'COMMITTEE_VICE_CHAIRMAN', label: '管委會副主委' },
  { value: 'COMMITTEE_MEMBER', label: '專職委員' },
  { value: 'COMMITTEE_SUPERVISOR', label: '監察委員' },
  { value: 'COMMITTEE_FINANCE', label: '財務委員' },
  { value: 'SUPERVISOR', label: '監督者' },
  { value: 'REPORTER', label: '報告查看者' }
]

interface ExtendedProjectMember {
  id: string
  role: string | {
    id: string
    name: string
    color?: string
    description?: string
  }
  roleId?: string
  joinedAt: string
  user: {
    id: string
    name: string
    email: string
    avatar?: string
  }
}

const ProjectSettings = () => {
  const { currentProject } = useAppSelector(state => state.project)
  const { user } = useAppSelector(state => state.auth)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions(currentProject?.id)
  const { showSuccess, showError } = useToast()
  const [trashRetentionDays, setTrashRetentionDays] = useState(currentProject?.trashRetentionDays || 365)
  
  // 除錯日誌
  useEffect(() => {
    console.log('ProjectSettings - currentProject:', currentProject)
    console.log('ProjectSettings - currentProject.id:', currentProject?.id)
  }, [currentProject])
  
  const [loading, setLoading] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [members, setMembers] = useState<ExtendedProjectMember[]>([])
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null)
  const [newRole, setNewRole] = useState('')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [availableRoles, setAvailableRoles] = useState<CustomRole[]>([])
  const [rolesLoading, setRolesLoading] = useState(false)
  const [iconUrl, setIconUrl] = useState('')
  const [savingIcon, setSavingIcon] = useState(false)
  const [uploadingIcon, setUploadingIcon] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 自訂欄位狀態
  const [customFields, setCustomFields] = useState<{
    enabledFields: customFieldApi.ProjectCustomField[]
    availableFields: customFieldApi.CustomField[]
  }>({ enabledFields: [], availableFields: [] })
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false)
  const [updatingCustomField, setUpdatingCustomField] = useState<string | null>(null)

  // 專案權限檢查
  const canEditProject = hasPermission(Permission.EDIT_PROJECT)
  const canManageMembers = hasPermission(Permission.MANAGE_PROJECT_MEMBERS)
  const canManageSettings = hasPermission(Permission.MANAGE_PROJECT_SETTINGS)

  // 獲取專案成員列表和角色
  useEffect(() => {
    if (currentProject && currentProject.id) {
      fetchMembers()
      fetchProjectRoles()
      fetchCustomFields()
    }
  }, [currentProject])

  const fetchMembers = async () => {
    if (!currentProject) return
    
    try {
      const response = await fetch(`/api/projects/${currentProject.id}/members`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setMembers(data.data?.members || [])
      }
    } catch (error) {
      console.error('獲取成員列表失敗:', error)
    }
  }

  const fetchProjectRoles = async () => {
    if (!currentProject) return

    try {
      setRolesLoading(true)
      const response = await getProjectAvailableRoles(currentProject.id)

      if (response.success) {
        setAvailableRoles(response.data?.roles || [])
      } else {
        console.error('獲取專案角色失敗:', response.error)
      }
    } catch (error: any) {
      console.error('獲取專案角色失敗:', error)
    } finally {
      setRolesLoading(false)
    }
  }

  const fetchCustomFields = async () => {
    if (!currentProject) return

    try {
      setCustomFieldsLoading(true)
      const data = await customFieldApi.getProjectCustomFields(currentProject.id)
      setCustomFields(data)
    } catch (error: any) {
      console.error('獲取專案自訂欄位失敗:', error)
      showError('載入失敗', '無法載入自訂欄位設定')
    } finally {
      setCustomFieldsLoading(false)
    }
  }

  const handleUpdateTrashRetention = async () => {
    if (!currentProject) return
    
    try {
      setLoading(true)
      await projectApi.updateProject(currentProject.id, {
        trashRetentionDays
      })
      showSuccess('設定更新', '回收桶保留天數已更新')
    } catch (error) {
      console.error('更新失敗:', error)
      showError('更新失敗', '無法更新回收桶設定')
    } finally {
      setLoading(false)
    }
  }

  // 處理專案欄位更新
  const handleUpdateProject = async (field: string, value: string) => {
    if (!currentProject) throw new Error('專案不存在')
    
    try {
      const updatedProject = await projectApi.updateProject(currentProject.id, {
        [field]: value
      })
      
      // 更新 Redux store 中的專案資訊
      dispatch(setCurrentProject(updatedProject))
      showSuccess('更新成功', `${field === 'name' ? '專案名稱' : '專案描述'}已更新`)
    } catch (error) {
      console.error('更新失敗:', error)
      throw new Error('無法更新專案資訊')
    }
  }

  // 更新成員角色
  const handleUpdateMemberRole = async (memberId: string, newRoleId: string) => {
    if (!currentProject) return
    
    try {
      const response = await fetch(`/api/projects/${currentProject.id}/members/${memberId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ roleId: newRoleId })
      })
      
      if (response.ok) {
        showSuccess('角色更新成功')
        setEditingMemberId(null)
        await fetchMembers()
      } else {
        const error = await response.json()
        showError('更新失敗', error.message || '無法更新成員角色')
      }
    } catch (error) {
      console.error('更新角色失敗:', error)
      showError('更新失敗', '無法更新成員角色')
    }
  }

  // 移除專案成員
  const handleRemoveMember = async (memberId: string) => {
    if (!currentProject) return
    
    if (!confirm('確定要移除此成員嗎？')) return
    
    try {
      const member = members.find(m => m.id === memberId)
      const response = await fetch(`/api/projects/${currentProject.id}/members/${member?.user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })
      
      if (response.ok) {
        showSuccess('成員移除成功')
        await fetchMembers()
      } else {
        const error = await response.json()
        showError('移除失敗', error.message || '無法移除成員')
      }
    } catch (error) {
      console.error('移除成員失敗:', error)
      showError('移除失敗', '無法移除成員')
    }
  }

  // 邀請成功後的回調
  const handleInviteSuccess = () => {
    fetchMembers()
  }

  // 處理封存專案
  const handleArchiveProject = async () => {
    if (!currentProject) return
    
    const confirmMessage = '確定要封存此專案嗎？封存後專案將不會顯示在專案列表中，但可以從管理介面還原。'
    
    if (!confirm(confirmMessage)) return
    
    try {
      setArchiving(true)
      await projectApi.updateProject(currentProject.id, {
        archived: true
      })
      
      showSuccess('封存成功', '專案已被封存')
      
      // 延遲一秒後導航到專案列表
      setTimeout(() => {
        navigate('/projects')
      }, 1000)
    } catch (error) {
      console.error('封存專案失敗:', error)
      showError('封存失敗', '無法封存專案')
    } finally {
      setArchiving(false)
    }
  }

  // 處理切換公開瀏覽
  const handleTogglePublicView = async () => {
    if (!currentProject) return

    try {
      const newStatus = !currentProject.allowPublicView
      const updatedProject = await projectApi.updateProject(currentProject.id, {
        allowPublicView: newStatus
      })

      dispatch(setCurrentProject(updatedProject))
      showSuccess(
        '更新成功',
        newStatus ? '已開啟專案公開瀏覽' : '已關閉專案公開瀏覽'
      )
    } catch (error) {
      console.error('更新公開瀏覽設定失敗:', error)
      showError('更新失敗', '無法更新公開瀏覽設定')
    }
  }

  // 處理切換財務管理模組
  const handleToggleFinance = async () => {
    if (!currentProject) return

    try {
      const newStatus = !currentProject.financeEnabled
      const updatedProject = await projectApi.updateProject(currentProject.id, {
        financeEnabled: newStatus
      })

      dispatch(setCurrentProject(updatedProject))
      showSuccess(
        '更新成功',
        newStatus ? '已啟用財務管理模組' : '已停用財務管理模組'
      )
    } catch (error) {
      console.error('更新財務管理設定失敗:', error)
      showError('更新失敗', '無法更新財務管理設定')
    }
  }

  // 處理圖示更新
  const handleIconUpdate = async () => {
    if (!currentProject || !iconUrl.trim()) return
    
    try {
      setSavingIcon(true)
      const updatedProject = await projectApi.updateProject(currentProject.id, {
        icon: iconUrl.trim()
      })
      
      dispatch(setCurrentProject(updatedProject))
      showSuccess('更新成功', '專案圖示已更新')
      setIconUrl('')
    } catch (error) {
      console.error('更新圖示失敗:', error)
      showError('更新失敗', '無法更新專案圖示')
    } finally {
      setSavingIcon(false)
    }
  }

  // 處理移除圖示
  const handleRemoveIcon = async () => {
    if (!currentProject || !currentProject.icon) return
    
    if (!confirm('確定要移除專案圖示嗎？')) return
    
    try {
      setSavingIcon(true)
      const updatedProject = await projectApi.updateProject(currentProject.id, {
        icon: ''
      })
      
      dispatch(setCurrentProject(updatedProject))
      showSuccess('移除成功', '專案圖示已移除')
    } catch (error) {
      console.error('移除圖示失敗:', error)
      showError('移除失敗', '無法移除專案圖示')
    } finally {
      setSavingIcon(false)
    }
  }

  // 處理檔案選擇
  const handleFileSelect = () => {
    fileInputRef.current?.click()
  }

  // 處理檔案上傳
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // 確保 currentProject 存在
    if (!currentProject || !currentProject.id) {
      showError('錯誤', '無法取得專案資訊，請重新整理頁面')
      return
    }

    // 檔案大小檢查 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showError('檔案過大', '圖示檔案大小不能超過 10MB')
      return
    }

    // 檔案類型檢查
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      showError('檔案格式不支援', '請上傳 JPG、PNG、GIF 或 SVG 格式的圖片')
      return
    }

    try {
      setUploadingIcon(true)

      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('/api/upload/project-icon', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '上傳失敗')
      }

      const data = await response.json()
      console.log('Upload response:', data)
      
      // 上傳成功後，更新專案圖示
      const updatedProject = await projectApi.updateProject(currentProject.id, {
        icon: data.url
      })
      
      console.log('Updated project:', updatedProject)
      
      if (updatedProject && updatedProject.id) {
        dispatch(setCurrentProject(updatedProject))
        showSuccess('上傳成功', '專案圖示已更新')
      } else {
        throw new Error('更新專案失敗：返回的專案資料不完整')
      }
      
      // 清空檔案選擇
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error: any) {
      console.error('圖示上傳失敗:', error)
      showError('上傳失敗', error.message || '無法上傳圖示')
    } finally {
      setUploadingIcon(false)
    }
  }

  // 處理自訂欄位啟用/停用
  const handleToggleCustomField = async (fieldId: string, enabled: boolean) => {
    if (!currentProject) return

    // 如果是停用操作，先確認
    if (!enabled) {
      const confirmed = confirm(
        '確定要停用此自訂欄位嗎？\n\n' +
        '停用後：\n' +
        '• 欄位將不會在議題中顯示\n' +
        '• 已填寫的資料會完整保留\n' +
        '• 重新啟用時資料會恢復顯示'
      )
      if (!confirmed) return
    }

    try {
      setUpdatingCustomField(fieldId)

      if (enabled) {
        await customFieldApi.enableProjectCustomField(currentProject.id, fieldId)
        showSuccess('欄位啟用', '自訂欄位已啟用，現在會顯示在議題中')
      } else {
        await customFieldApi.disableProjectCustomField(currentProject.id, fieldId)
        showSuccess('欄位停用', '自訂欄位已停用，資料已安全保留')
      }

      // 重新載入自訂欄位設定
      await fetchCustomFields()
    } catch (error: any) {
      console.error('切換自訂欄位失敗:', error)
      showError('操作失敗', error.message || '無法切換自訂欄位狀態')
    } finally {
      setUpdatingCustomField(null)
    }
  }

  // 處理自訂欄位必填設定
  const handleToggleCustomFieldRequired = async (fieldId: string, required: boolean) => {
    if (!currentProject) return

    try {
      setUpdatingCustomField(fieldId)

      // 先啟用欄位（如果尚未啟用），然後設定必填狀態
      await customFieldApi.enableProjectCustomField(currentProject.id, fieldId, { required })
      showSuccess('設定更新', '必填設定已更新')

      // 重新載入自訂欄位設定
      await fetchCustomFields()
    } catch (error: any) {
      console.error('更新必填設定失敗:', error)
      showError('操作失敗', error.message || '無法更新必填設定')
    } finally {
      setUpdatingCustomField(null)
    }
  }

  // 處理自訂欄位卡片顯示設定
  const handleToggleCustomFieldShowInCard = async (fieldId: string, showInCard: boolean) => {
    if (!currentProject) return

    try {
      setUpdatingCustomField(fieldId)

      await customFieldApi.updateProjectCustomFieldShowInCard(currentProject.id, fieldId, showInCard)
      showSuccess(
        '設定更新',
        showInCard ? '欄位將顯示在議題卡片上' : '欄位已從議題卡片中隱藏'
      )

      // 重新載入自訂欄位設定
      await fetchCustomFields()
    } catch (error: any) {
      console.error('更新卡片顯示設定失敗:', error)
      showError('操作失敗', error.message || '無法更新卡片顯示設定')
    } finally {
      setUpdatingCustomField(null)
    }
  }

  // 取得欄位類型顯示文字
  const getFieldTypeLabel = (type: string) => {
    switch (type) {
      case 'TEXT': return '文字'
      case 'NUMBER': return '數字'
      case 'DATE': return '日期'
      case 'SELECT': return '下拉選單'
      default: return type
    }
  }

  const getRoleLabel = (member: ExtendedProjectMember) => {
    const role = member.role

    if (role && typeof role === 'object') {
      return role.name
    }

    const roleOption = PROJECT_ROLES.find(r => r.value === role)
    return roleOption?.label || role || '未知角色'
  }

  const getRoleColor = (member: ExtendedProjectMember) => {
    const role = member.role

    if (role && typeof role === 'object' && role.color) {
      const color = role.color
      // 將十六進制顏色轉換為 Tailwind 類別
      if (color.includes('purple') || color === '#dc2626') return 'bg-purple-100 text-purple-800'
      if (color.includes('red') || color === '#ef4444') return 'bg-red-100 text-red-800'
      if (color.includes('blue') || color === '#2563eb') return 'bg-blue-100 text-blue-800'
      if (color.includes('green') || color === '#16a34a') return 'bg-green-100 text-green-800'
      if (color.includes('yellow')) return 'bg-yellow-100 text-yellow-800'
    }
    
    // 根據角色名稱提供預設顏色
    const roleName = typeof role === 'object' ? role.name : role || ''
    if (roleName.includes('擁有者') || roleName.includes('主委') || roleName.includes('經理')) {
      return 'bg-purple-100 text-purple-800'
    }
    if (roleName.includes('管理') || roleName.includes('副理')) {
      return 'bg-red-100 text-red-800'
    }
    if (roleName.includes('開發') || roleName.includes('執行')) {
      return 'bg-blue-100 text-blue-800'
    }
    if (roleName.includes('監督') || roleName.includes('監察')) {
      return 'bg-green-100 text-green-800'
    }
    
    return 'bg-gray-100 text-gray-800'
  }

  if (!currentProject || !currentProject.id) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">載入中...</h3>
        <p className="text-gray-500">正在載入專案資料</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">

      <div className="space-y-6">
        {/* 基本資訊 */}
        <PermissionGate 
          permission={Permission.EDIT_PROJECT} 
          projectId={currentProject?.id}
          fallback={
            <div className="card p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">基本資訊</h2>
              <div className="space-y-4 text-gray-500">
                <p>您沒有編輯專案基本資訊的權限。</p>
              </div>
            </div>
          }
        >
          <div className="card p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">基本資訊</h2>
            <div className="space-y-4">
              <EditableField
                label="專案名稱"
                value={currentProject.name}
                onSave={(value) => handleUpdateProject('name', value)}
                disabled={!canEditProject}
                placeholder="輸入專案名稱"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  專案代碼
                </label>
                <div className="px-3 py-2 bg-gray-50 rounded-md text-gray-600">
                  {currentProject.key}
                </div>
                <p className="text-xs text-gray-500 mt-1">專案代碼不可修改</p>
              </div>
              
              <EditableField
                label="專案描述"
                value={currentProject.description || ''}
                onSave={(value) => handleUpdateProject('description', value)}
                disabled={!canEditProject}
                type="textarea"
                rows={3}
                placeholder="輸入專案描述"
              />
            </div>
          </div>
        </PermissionGate>

        {/* 專案設定 */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">專案設定</h2>
          <div className="space-y-6">
            {/* 公開瀏覽設定 */}
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">允許非專案成員瀏覽</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    開啟後，非專案成員也可以瀏覽專案內容（唯讀權限）
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleTogglePublicView}
                  disabled={!canManageSettings}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    currentProject.allowPublicView ? 'bg-primary-600' : 'bg-gray-200'
                  } ${!canManageSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      currentProject.allowPublicView ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* 財務管理模組設定 */}
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">啟用財務管理模組</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    開啟後，專案側邊欄將顯示財務管理入口，提供社區管理費收繳功能
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleFinance}
                  disabled={!canManageSettings}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                    currentProject.financeEnabled ? 'bg-green-600' : 'bg-gray-200'
                  } ${!canManageSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      currentProject.financeEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* 回收桶設定 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">回收桶保留天數</h3>
              <p className="text-xs text-gray-500 mb-3">
                議題刪除後在回收桶中保留的天數，超過此時間將自動永久刪除（最少 120 天）
              </p>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  min="120"
                  max="3650"
                  value={trashRetentionDays}
                  onChange={(e) => setTrashRetentionDays(parseInt(e.target.value) || 365)}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={!canManageSettings}
                />
                <span className="text-sm text-gray-600">天</span>
                {canManageSettings && (
                  <button
                    type="button"
                    onClick={handleUpdateTrashRetention}
                    disabled={loading || trashRetentionDays === currentProject.trashRetentionDays}
                    className="px-3 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? '更新中...' : '更新'}
                  </button>
                )}
              </div>
            </div>

            {/* 專案圖示 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">專案圖示</h3>
              <div className="flex items-start space-x-4">
                {/* 當前圖示 */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {currentProject.icon ? (
                      <img 
                        src={getImageUrl(currentProject.icon) || currentProject.icon} 
                        alt={currentProject.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent && !parent.querySelector('svg')) {
                            const fallback = document.createElement('div');
                            fallback.innerHTML = '<svg class="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-4.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" /></svg>';
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    ) : (
                      <PhotoIcon className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* 圖示操作 */}
                <div className="flex-1 space-y-3">
                  {/* 集中在一行的操作區域 */}
                  <div className="flex items-center space-x-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={handleFileSelect}
                      disabled={!canManageSettings || uploadingIcon}
                      className="px-3 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center whitespace-nowrap"
                    >
                      <CloudArrowUpIcon className="w-4 h-4 mr-1.5" />
                      {uploadingIcon ? '上傳中...' : '上傳圖片'}
                    </button>
                    
                    <span className="text-sm text-gray-500">或</span>
                    
                    <input
                      type="url"
                      value={iconUrl}
                      onChange={(e) => setIconUrl(e.target.value)}
                      placeholder="輸入圖示 URL"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={!canManageSettings}
                    />
                    
                    <button
                      type="button"
                      onClick={handleIconUpdate}
                      disabled={!canManageSettings || !iconUrl.trim() || savingIcon}
                      className="px-3 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {savingIcon ? '更新中...' : '使用 URL'}
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      支援 JPG、PNG、GIF、SVG 格式，檔案大小不超過 10MB，建議使用正方形圖片
                    </p>
                    
                    {currentProject.icon && canManageSettings && (
                      <button
                        type="button"
                        onClick={handleRemoveIcon}
                        disabled={savingIcon || uploadingIcon}
                        className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        移除圖示
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 自訂欄位管理 */}
        <PermissionGate
          permission={Permission.MANAGE_PROJECT_SETTINGS}
          projectId={currentProject?.id}
        >
          <div className="card p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">自訂欄位管理</h2>

            {customFieldsLoading ? (
              <div className="text-center py-4">
                <p className="text-gray-500">載入中...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  選擇要在此專案中使用的自訂欄位。啟用的欄位會顯示在議題詳情中。
                </p>

{(() => {
                  // 創建所有欄位的統一列表（包含 availableFields 和只在 enabledFields 中的欄位）
                  const allFields = new Map()

                  // 先添加所有可用欄位
                  customFields.availableFields?.forEach(field => {
                    allFields.set(field.id, field)
                  })

                  // 再添加已啟用但不在可用欄位中的欄位
                  customFields.enabledFields?.forEach(enabledField => {
                    if (!allFields.has(enabledField.fieldId) && enabledField.field) {
                      allFields.set(enabledField.fieldId, enabledField.field)
                    }
                  })

                  const fieldsArray = Array.from(allFields.values())

                  return fieldsArray.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>尚無可用的自訂欄位</p>
                      <p className="text-xs mt-1">請先在系統管理中建立自訂欄位</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {fieldsArray.map((field) => {
                        const enabledField = customFields.enabledFields?.find(ef => ef.fieldId === field.id)
                        const isEnabled = !!enabledField
                        const isUpdating = updatingCustomField === field.id

                        return (
                          <div key={field.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h3 className="text-sm font-medium text-gray-900">{field.name}</h3>
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {getFieldTypeLabel(field.type)}
                                  </span>
                                  {field.required && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      預設必填
                                    </span>
                                  )}
                                </div>

                                <p className="text-xs text-gray-500 mb-1">鍵值: {field.key}</p>

                                {field.type === 'SELECT' && field.options && (
                                  <p className="text-xs text-gray-500">
                                    選項: {field.options.join(', ')}
                                  </p>
                                )}
                              </div>

                              <div className="flex items-center space-x-4">
                                {/* 啟用/停用開關 */}
                                <div className="flex items-center space-x-3">
                                  <span className="text-xs text-gray-700">啟用</span>
                                  <button
                                    type="button"
                                    onClick={() => handleToggleCustomField(field.id, !isEnabled)}
                                    disabled={isUpdating}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                      isEnabled ? 'bg-primary-600' : 'bg-gray-200'
                                    } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                    title={isEnabled ? '點擊停用此欄位' : '點擊啟用此欄位'}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        isEnabled ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                </div>

                                {/* 必填設定和卡片顯示設定（只有啟用時才顯示） */}
                                {isEnabled && (
                                  <div className="flex items-center space-x-3">
                                    {/* 必填設定 */}
                                    <div className="flex items-center space-x-2">
                                      <span className="text-xs text-gray-700">必填</span>
                                      <button
                                        type="button"
                                        onClick={() => handleToggleCustomFieldRequired(field.id, !enabledField?.required)}
                                        disabled={isUpdating}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                                          enabledField?.required ? 'bg-red-600' : 'bg-gray-200'
                                        } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        title={enabledField?.required ? '點擊設為選填' : '點擊設為必填'}
                                      >
                                        <span
                                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            enabledField?.required ? 'translate-x-6' : 'translate-x-1'
                                          }`}
                                        />
                                      </button>
                                    </div>

                                    {/* 卡片顯示設定 */}
                                    <div className="flex items-center space-x-2">
                                      <span className="text-xs text-gray-700">顯示於看板頁</span>
                                      <button
                                        type="button"
                                        onClick={() => handleToggleCustomFieldShowInCard(field.id, !enabledField?.showInCard)}
                                        disabled={isUpdating}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                          enabledField?.showInCard ? 'bg-blue-600' : 'bg-gray-200'
                                        } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        title={enabledField?.showInCard ? '點擊隱藏在議題卡片上' : '點擊顯示在議題卡片上'}
                                      >
                                        <span
                                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            enabledField?.showInCard ? 'translate-x-6' : 'translate-x-1'
                                          }`}
                                        />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {isUpdating && (
                              <div className="mt-2 text-xs text-gray-500 flex items-center">
                                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-gray-600 mr-2"></div>
                                更新中...
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}

              </div>
            )}
          </div>
        </PermissionGate>

        {/* 專案成員管理 */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">專案成員</h2>
            <PermissionGate 
              permission={Permission.MANAGE_PROJECT_MEMBERS} 
              projectId={currentProject?.id}
            >
              <button 
                onClick={() => setShowInviteModal(true)}
                className="btn-primary flex items-center text-sm"
              >
                <UserPlusIcon className="w-4 h-4 mr-1" />
                邀請成員
              </button>
            </PermissionGate>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center flex-1 min-w-0">
                  {member.user.avatar ? (
                    <img
                      src={member.user.avatar}
                      alt={member.user.name}
                      className="w-8 h-8 rounded-full flex-shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-gray-600">
                        {member.user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {member.user.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {member.user.email}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {editingMemberId === member.id ? (
                    <div className="flex items-center space-x-2">
                      <select
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500 min-w-24"
                        disabled={rolesLoading}
                      >
                        <option value="">選擇角色</option>
                        {availableRoles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleUpdateMemberRole(member.id, newRole)}
                        disabled={!newRole || rolesLoading}
                        className="text-green-600 hover:text-green-700 text-xs disabled:opacity-50"
                      >
                        確認
                      </button>
                      <button
                        onClick={() => setEditingMemberId(null)}
                        className="text-gray-500 hover:text-gray-700 text-xs"
                      >
                        取消
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(member)}`}>
                        {getRoleLabel(member)}
                      </span>
                      
                      {canManageMembers && member.user.id !== user?.id && (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => {
                              setEditingMemberId(member.id)
                              // 設置當前角色 ID
                              const currentRoleId = typeof member.role === 'object' 
                                ? member.role.id 
                                : member.roleId || member.role
                              setNewRole(currentRoleId)
                            }}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            title="編輯角色"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="移除成員"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {members.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>尚無專案成員</p>
            </div>
          )}
        </div>


        {/* 危險區域 - 封存專案 */}
        <PermissionGate 
          permission={Permission.ARCHIVE_PROJECT} 
          projectId={currentProject?.id}
        >
          <div className="card p-6 border-red-200">
            <h2 className="text-lg font-medium text-red-900 mb-4">危險區域</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">封存專案</h3>
                <p className="text-xs text-gray-500 mb-4">
                  封存專案會將其從一般專案列表中移除，但資料仍會保留。管理員可以從管理介面還原已封存的專案。
                </p>
                <button
                  type="button"
                  onClick={handleArchiveProject}
                  disabled={archiving}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <ArchiveBoxIcon className="w-4 h-4 mr-2" />
                  {archiving ? '封存中...' : '封存專案'}
                </button>
              </div>
            </div>
          </div>
        </PermissionGate>
      </div>

      {/* 邀請成員模態框 */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        projectId={currentProject.id}
        currentMembers={members}
        onSuccess={handleInviteSuccess}
      />

    </div>
  )
}

export default ProjectSettings
